import { CommonModule, formatDate } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { catchError, combineLatest, distinctUntilChanged, filter, forkJoin, map, Observable, of, startWith, Subject, switchMap, take } from 'rxjs';
import {
  GURTBREITE_OPTIONS,
  ORDER_DOCUMENT_STATUS_LABELS,
  ORDER_DOCUMENT_STATUS_OPTIONS,
  PALLET_BOX_TYPE_LABELS,
  PALLET_BOX_TYPE_OPTIONS,
  WERK_LABELS,
  WERK_OPTIONS,
} from '../../shared/constants/order-document-labels';
import { EmployeeInfoButtonComponent } from '../../shared/components/employee-info-button/employee-info-button.component';
import { canAccess, FALSE_POSITION_ROLES } from '../../shared/constants/role-access';
import { SelectOnFocusDirective } from '../../shared/directives/select-on-focus.directive';
import { EmployeeIdModalComponent, EmployeeIdModalData } from '../../shared/modals/employee-id-modal/employee-id-modal.component';
import { PasteTextBlockModalComponent } from '../../shared/modals/paste-text-block-modal/paste-text-block-modal.component';
import {
  ActivityType,
  ILiefertermin,
  IOrderDocument,
  IOrderDocumentFalsePosition,
  IOrderDocumentPosition,
  IOrderDocumentSummary,
  OrderDocumentStatusEnum,
  ProcessingType,
  WerkType,
} from '../../shared/models/order-document.model';
import { IOrder } from '../../shared/models/order.model';
import { TextBlockType } from '../../shared/models/text-block.model';
import { IUser, UserRole } from '../../shared/models/user.model';
import { AuthService } from '../../shared/services/auth.service';
import { ModalService } from '../../shared/services/modal.service';
import { IOrderDocumentLock, OrderDocumentLockService } from '../../shared/services/order-document-lock.service';
import { OrderDocumentService } from '../../shared/services/order-document.service';
import { OrderService } from '../../shared/services/order.service';
import { PageTitleService } from '../../shared/services/page-title.service';
import { ToastService } from '../../shared/services/toast.service';
import { DocumentsComponent } from './components/documents/documents.component';
import { createFalsePositionGroup, FalsePositionsTableComponent } from './components/false-positions-table/false-positions-table.component';
import { createPositionGroup, PositionsTableComponent } from './components/positions-table/positions-table.component';

// The tabs below the header: Zeiterfassung (positions), Fehlererfassung (false positions) and
// Dokumente (documents).
type OrderDocumentTab = 'positions' | 'falsePositions' | 'documents';

// Every header field a Fertigungsmitarbeiter may NOT touch — everything except status and
// positions (the Zeiterfassung table stays fully editable, see canManagePositions). Kunde
// (order) and Liefertermine are handled separately since they're also gated by isEditMode().
const FERTIGUNGSMITARBEITER_LOCKED_FIELDS = [
  'tempCustomer',
  'palletNumber',
  'palletBoxType',
  'weDate',
  'orderQuantity',
  'partialQuantity',
  'componentType',
  'gurtbreite',
  'factory',
  'processing',
  'activities',
  'remark',
  'observeSpecification',
];

// Option label of the switcher between the Auftragsbegleitscheine of one order (there's no document
// number): "{n}. {Werk} · {Paletten-/Box-Typ} {Nummer} · {Status} · {erstellt}", n counting from the oldest.
// Werk falls back to Werk 1 for documents saved before it existed, like the form does on load.
function orderDocumentSummaryLabel(doc: IOrderDocumentSummary, index: number): string {
  const pallet = [doc.palletBoxType ? PALLET_BOX_TYPE_LABELS[doc.palletBoxType] : '', doc.palletNumber ?? '']
    .filter(Boolean)
    .join(' ');
  const created = doc.createdAt ? formatDate(doc.createdAt, 'dd.MM.yyyy', 'en-US') : '';
  const parts = [WERK_LABELS[doc.factory ?? WerkType.WERK_1], pallet, ORDER_DOCUMENT_STATUS_LABELS[doc.status], created].filter(Boolean);
  return `${index + 1}. ${parts.join(' · ')}`;
}

@Component({
  selector: 'app-create-order-document',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    CheckboxModule,
    MultiSelectModule,
    DatePickerModule,
    ToggleSwitchModule,
    SelectModule,
    TabsModule,
    SelectOnFocusDirective,
    PositionsTableComponent,
    FalsePositionsTableComponent,
    DocumentsComponent,
    EmployeeInfoButtonComponent,
  ],
  templateUrl: './create-order-document.component.html',
  styleUrls: ['./create-order-document.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateOrderDocumentComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly orderDocumentService = inject(OrderDocumentService);
  private readonly orderDocumentLockService = inject(OrderDocumentLockService);
  private readonly orderService = inject(OrderService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(ModalService);
  private readonly pageTitleService = inject(PageTitleService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  // True while the Mitarbeiternummer modal is shown, so leaving the page (e.g. browser back) closes it.
  private isEmployeeIdModalOpen = false;

  public form!: FormGroup;
  // Staging area for a Liefertermin not yet added to the deliveryDates FormArray (the "Hinzufügen"
  // row inside the dropdown). A separate FormGroup rather than plain fields, per the reactive-forms
  // convention — every user-editable value is a control.
  public newLieferterminForm!: FormGroup;
  public readonly UserRole = UserRole;
  public readonly activityOptions = [
    { value: ActivityType.SCHNEIDEN, label: 'Schneiden' },
    { value: ActivityType.BIEGEN, label: 'Biegen' },
    { value: ActivityType.TROCKNEN, label: 'Trocknen' },
    { value: ActivityType.VEREINZELN, label: 'Vereinzeln' },
    { value: ActivityType.SPLEISSEN, label: 'Spleißen' },
    { value: ActivityType.DREHEN, label: 'Drehen im Gurt' },
    { value: ActivityType.GURTEN, label: 'Gurten' },
    { value: ActivityType.ENTGURTEN_NEUGURTEN, label: 'Entgurten + Neugurten' },
    { value: ActivityType.KONFEKTIONIEREN, label: 'Konfektionieren' },
  ];
  public readonly processingOptions = [
    { value: ProcessingType.RADIAL, label: 'Radial' },
    { value: ProcessingType.SMD, label: 'SMD' },
  ];
  public readonly werkOptions = WERK_OPTIONS;
  public readonly gurtbreiteOptions = GURTBREITE_OPTIONS;
  public readonly statusOptions = ORDER_DOCUMENT_STATUS_OPTIONS;
  public readonly palletBoxTypeOptions = PALLET_BOX_TYPE_OPTIONS;

  public readonly isSubmitting = signal(false);
  public readonly isLoading = signal(false);
  public readonly isEditMode = signal(false);
  public readonly orderDocumentId = signal<string | null>(null);

  // _id of the linked order (Kunde) as last loaded/saved; null when none is linked. Drives the
  // switcher below and the order-wide document list of the Dokumente tab.
  public readonly orderId = signal<string | null>(null);

  // The Auftragsbegleitscheine of the linked order, oldest first. Re-fetched on every load/save
  // (see patchFormValues) so the labels follow status changes; nothing is fetched without an order.
  private readonly siblingsRefresh$ = new Subject<string | null>();
  private readonly siblings = toSignal(
    this.siblingsRefresh$.pipe(
      switchMap(orderId =>
        orderId
          ? this.orderDocumentService.getByOrderId(orderId).pipe(
              map(response => response.DATA ?? []),
              catchError(() => {
                this.toastService.showError('Die Auftragsbegleitscheine des Auftrags konnten nicht geladen werden');
                return of<IOrderDocumentSummary[]>([]);
              })
            )
          : of<IOrderDocumentSummary[]>([])
      )
    ),
    { initialValue: [] }
  );
  public readonly siblingOptions = computed(() =>
    this.siblings().map((doc, index) => ({ value: doc._id ?? '', label: orderDocumentSummaryLabel(doc, index) }))
  );
  // Same labels, by id — for the owner badge on the Dokumente tab's rows.
  public readonly siblingLabels = computed<Record<string, string>>(() =>
    Object.fromEntries(this.siblingOptions().map(option => [option.value, option.label]))
  );
  // Only offered once the document is saved, and only when there's actually something to switch to.
  public readonly showSiblingSelect = computed(() => this.isEditMode() && this.siblings().length > 1);
  // The switcher. A standalone control, not part of `form`: picking an entry navigates instead of
  // editing the document, so it must never make the form dirty.
  public readonly siblingControl = new FormControl<string | null>(null);

  public readonly isLockLoading = signal(false);
  public readonly isLocked = signal(false);
  public readonly lockInfo = signal<IOrderDocumentLock | null>(null);

  // Positionen header-pin ("Kopfzeile fixieren") state, two-way bound to the positions table —
  // kept here so selectTab() can release it when leaving the Zeiterfassung tab.
  public readonly isHeaderPinned = signal(false);

  // Rendered while hidden too (p-tabpanel isn't lazy), so "Fehler erfassen" can add a row to it
  // from the Zeiterfassung tab.
  private readonly falsePositionsTable = viewChild(FalsePositionsTableComponent);

  // Snapshot of the positions as last loaded from the server, so Speichern can tell which rows were removed
  private originalPositions: IOrderDocumentPosition[] = [];

  // Same as originalPositions, for the Fehlererfassung rows
  private originalFalsePositions: IOrderDocumentFalsePosition[] = [];

  // Active tab below the header, and the row count shown as a badge on the Fehlererfassung tab
  // (kept in sync from the falsePositions FormArray's valueChanges, see initForm()).
  public readonly activeTab = signal<OrderDocumentTab>('positions');

  // Kunde (order) search state
  public readonly orderSuggestions = signal<IOrder[]>([]);
  public readonly isOrderSearchLoading = signal(false);
  public readonly minOrderSearchLength = 3;
  private readonly orderSearchTerms = new Subject<string>();

  private readonly currentUser = toSignal(this.authService.currentUser$, { initialValue: null });
  public readonly userRole = computed(() => this.currentUser()?.role ?? null);

  // Fertigungsmitarbeiter may only change Status and Positionen (Zeiterfassung) — every other
  // header field stays locked. See the effects below and STATUS_ONLY_FIELDS on the backend.
  public readonly isFertigungsmitarbeiter = computed(() => this.userRole() === UserRole.FERTIGUNGSMITARBEITER);

  // Only admins see the info icon that reveals the name behind the Mitarbeiter-Nr.
  public readonly isAdmin = computed(() => this.userRole() === UserRole.ADMIN);

  // True once the Kunde (order) field carries a real customer, kept in sync via the
  // 'order' control's valueChanges (subscribed in initForm(), since the control doesn't
  // exist yet when this field initializer runs).
  public readonly hasCustomer = signal(false);


  // Temporärer Kunde is visible while creating, and in edit mode until a real customer is assigned.
  public readonly showTempCustomerField = computed(() => !this.isEditMode() || !this.hasCustomer());

  // Positionen may only be managed once the document exists (edit mode) and a Kunde is assigned —
  // until then the Zeiterfassung shows a hint instead of the table. See the effect below,
  // createPositionGroup() and saveEditedDocument().
  public readonly canManagePositions = computed(() => this.isEditMode() && this.hasCustomer());

  // Every role sees the Fehlererfassung tab and reads its rows, but only ADMIN and ENDKONTROLLE may
  // add ("Fehler erfassen" / "Fehler hinzufügen"), edit or delete them — mirrors the requireRoles gate
  // on the backend's false-positions routes. For every other role the rows stay disabled, so a stored
  // row they can't fix (e.g. a migrated one without Prüfer) never makes their form invalid.
  public readonly canEditFalsePositions = computed(() => canAccess(this.userRole(), FALSE_POSITION_ROLES));
  // Like Positionen, Fehler can only be recorded once a Kunde is assigned.
  public readonly canManageFalsePositions = computed(() => this.isEditMode() && this.hasCustomer() && this.canEditFalsePositions());

  // Speichern is always offered — every logged-in user may save.
  public readonly canSave = computed(() => true);

  constructor() {
    // effect() requires an injection context, so these are wired up in the constructor. Their
    // first run is scheduled asynchronously (not synchronous with creation), so `this.form` is
    // already set by ngOnInit's initForm() call by the time either of them actually executes.

    // Enable/disable restricted fields through the control whenever the gate changes. Kunde and
    // Liefertermine are hidden entirely in create mode (gated by isEditMode() in the template),
    // so the controls stay disabled there too.
    effect(() => {
      const canEdit = this.isEditMode() && !this.isFertigungsmitarbeiter();
      const opts = { emitEvent: false };
      this.form?.get('order')?.[canEdit ? 'enable' : 'disable'](opts);
      this.form?.get('deliveryDates')?.[canEdit ? 'enable' : 'disable'](opts);
    });

    // Fertigungsmitarbeiter: lock every other header field ("the upper part") — Status and
    // Positionen (handled elsewhere) stay untouched. Also locks the Liefertermine "add" picker,
    // which lives in the separate newLieferterminForm and wouldn't otherwise be caught by the
    // deliveryDates FormArray's own disabled state (see createLieferterminGroup/addLiefertermin).
    effect(() => {
      const locked = this.isFertigungsmitarbeiter();
      const opts = { emitEvent: false };
      FERTIGUNGSMITARBEITER_LOCKED_FIELDS.forEach(key => this.form?.get(key)?.[locked ? 'disable' : 'enable'](opts));
      this.newLieferterminForm?.get('date')?.[locked ? 'disable' : 'enable'](opts);
    });

    // Enable/disable existing Positionen and Fehlererfassung rows through their controls whenever
    // their manage-gate changes (e.g. once the document/role is resolved). Rows created afterwards are
    // built with the correct disabled state directly in createPositionGroup()/createFalsePositionGroup()
    // (see below and the two table components).
    effect(() => {
      const gates: [FormArray | undefined, boolean][] = [
        [this.positions, this.canManagePositions()],
        [this.falsePositions, this.canManageFalsePositions()],
      ];
      const opts = { emitEvent: false };
      gates.forEach(([array, canManage]) =>
        array?.controls.forEach(group => {
          Object.keys((group as FormGroup).controls)
            .filter(key => key !== '_id')
            .forEach(key => group.get(key)?.[canManage ? 'enable' : 'disable'](opts));
        })
      );
    });

    // Keep the switcher on the document actually shown — also after a cancelled switch.
    effect(() => {
      this.siblingControl.setValue(this.orderDocumentId(), { emitEvent: false });
    });

    // These fields are mandatory only while creating a document — whoever creates it must describe
    // what was received. Once the document exists (edit mode), the requirement is dropped — the
    // fields stay editable but are no longer gate-kept on save. Temporärer Kunde (like Kunde and
    // Liefertermine) is optional throughout.
    effect(() => {
      const requiredWhileCreating = ['componentType', 'palletNumber', 'palletBoxType', 'processing', 'activities'];
      const validator = this.isEditMode() ? null : Validators.required;
      requiredWhileCreating.forEach(key => {
        const control = this.form?.get(key);
        if (!control) return;
        control.setValidators(validator);
        control.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  ngOnInit(): void {
    this.initForm();

    // Create mode: ask who is creating the document. After the first render, so the modal isn't
    // created in the middle of this page's change detection.
    if (!this.route.snapshot.paramMap.get('id')) {
      afterNextRender(() => this.confirmEmployeeId(), { injector: this.injector });
    }

    // Kunde search (debounce + min-length are handled by p-autoComplete;
    // switchMap keeps results in order / cancels stale requests)
    this.orderSearchTerms
      .pipe(
        switchMap(term => {
          const q = (term || '').trim();
          if (q.length < this.minOrderSearchLength) {
            this.isOrderSearchLoading.set(false);
            return of<IOrder[]>([]);
          }
          return this.orderService.getOrders(1, 10, q, 'internalOrderId', 'asc').pipe(map(response => response.DATA?.data || []));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: results => {
          this.orderSuggestions.set(results);
          this.isOrderSearchLoading.set(false);
        },
        error: () => {
          this.isOrderSearchLoading.set(false);
          this.orderSuggestions.set([]);
          this.toastService.showError('Fehler beim Laden der Auftragsbegleitscheine');
        },
      });

    // Edit mode. Subscribed rather than read once from the snapshot: switching to another
    // Auftragsbegleitschein of the same order navigates to /edit/:otherId, and the router reuses this
    // component for it (unsavedChangesGuard has run by then).
    this.route.paramMap
      .pipe(
        map(params => params.get('id')),
        filter((id): id is string => !!id),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(id => this.openOrderDocument(id));

    // Switching via the dropdown. If the navigation is cancelled (e.g. Speichern in the guard's
    // dialog failed), the effect in the constructor has nothing to re-sync — put the control back here.
    this.siblingControl.valueChanges
      .pipe(
        filter((id): id is string => !!id && id !== this.orderDocumentId()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(id => {
        this.router.navigate(['/order-documents/edit', id]).then(navigated => {
          if (!navigated) this.siblingControl.setValue(this.orderDocumentId(), { emitEvent: false });
        });
      });
  }

  // Accounts are shared, so the creator confirms their own Mitarbeiternummer (prefilled with the
  // account's). The modal validates it against the backend before it closes; Abbrechen leaves the page,
  // since a document can't be created without it.
  private confirmEmployeeId(): void {
    this.authService.currentUser$
      .pipe(
        filter((user): user is IUser => !!user?._id),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(user => {
        const data: EmployeeIdModalData = { employeeId: user.employeeId?.toString() || '' };
        this.isEmployeeIdModalOpen = true;
        this.modalService
          .open(EmployeeIdModalComponent, data)
          .then((employeeId: string | null) => {
            this.isEmployeeIdModalOpen = false;
            if (employeeId) {
              this.form.patchValue({ employeeId });
            } else {
              this.cancel();
            }
          })
          .catch(() => { });
      });
  }

  private openOrderDocument(id: string): void {
    // Hand back the lock on the document shown so far (a no-op on first open, or when it was only
    // shown read-only behind the lock overlay).
    this.releaseLock();
    this.isLocked.set(false);
    this.lockInfo.set(null);
    this.isEditMode.set(true);
    this.orderDocumentId.set(id);
    this.acquireLock(id);
  }

  // Mirrors create-delivery-note.component.ts's acquireLock(): waits for the logged-in user
  // (currentUser$ may not have emitted the real user yet on a hard refresh of the edit route)
  // before requesting the lock. Loads the document either way — on a conflict it's shown
  // read-only behind the lock overlay, same as delivery notes.
  private acquireLock(orderDocumentId: string): void {
    this.isLockLoading.set(true);

    this.authService.currentUser$
      .pipe(
        filter((user): user is IUser => !!user?._id),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(user => {
        this.orderDocumentLockService
          .createLock(orderDocumentId, String(user.employeeId || ''))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              // Switched to another document meanwhile: give this lock straight back.
              if (orderDocumentId !== this.orderDocumentId()) {
                this.orderDocumentLockService.deleteLock(orderDocumentId).subscribe();
                return;
              }
              this.isLocked.set(false);
              this.lockInfo.set(null);
              this.isLockLoading.set(false);
              this.loadOrderDocument(orderDocumentId);
            },
            error: error => {
              if (orderDocumentId !== this.orderDocumentId()) return;
              this.isLocked.set(true);
              this.lockInfo.set(error.error?.DATA || null);
              this.isLockLoading.set(false);
              this.loadOrderDocument(orderDocumentId);
            },
          });
      });
  }

  // Mirrors create-delivery-note.component.ts's releaseLock(): only releases a lock this
  // component actually holds — never fires for a document we only ever saw read-only.
  private releaseLock(): void {
    const docId = this.orderDocumentId();
    if (!this.isEditMode() || !docId || this.isLocked()) return;

    this.orderDocumentLockService.deleteLock(docId).subscribe();
  }

  private loadOrderDocument(id: string): void {
    this.isLoading.set(true);
    this.orderDocumentService
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          // A response for a document switched away from meanwhile must not overwrite the form.
          if (id !== this.orderDocumentId()) return;
          if (response.DATA) {
            this.patchFormValues(response.DATA);
          }
          this.isLoading.set(false);
        },
        error: () => {
          if (id !== this.orderDocumentId()) return;
          this.isLoading.set(false);
          this.toastService.showError('Fehler beim Laden des Auftragsbegleitscheins');
          this.router.navigate(['/order-documents']);
        },
      });
  }

  private patchFormValues(doc: IOrderDocument): void {
    this.form.patchValue({
      employeeId: doc.employeeId ?? '',
      tempCustomer: doc.tempCustomer ?? '',
      palletNumber: doc.palletNumber ?? '',
      palletBoxType: doc.palletBoxType ?? '',
      weDate: doc.weDate ? new Date(doc.weDate) : null,
      order: doc.order ?? null,
      orderQuantity: doc.orderQuantity ?? null,
      partialQuantity: doc.partialQuantity ?? null,
      componentType: doc.componentType ?? '',
      gurtbreite: doc.gurtbreite ?? null,
      factory: doc.factory ?? WerkType.WERK_1,
      processing: doc.processing ?? [],
      activities: doc.activities ?? [],
      remark: doc.remark ?? '',
      observeSpecification: doc.observeSpecification ?? false,
      status: doc.status ?? OrderDocumentStatusEnum.WARENEINGANG,
    });

    this.orderId.set(doc.order?._id ?? null);
    this.siblingsRefresh$.next(doc.order?._id ?? null);

    this.deliveryDates.clear();
    (doc.deliveryDates ?? []).forEach((entry: ILiefertermin) =>
      this.deliveryDates.push(this.createLieferterminGroup(new Date(entry.date), entry.isBinding, entry.label ?? ''))
    );

    this.originalPositions = doc.positions ? [...doc.positions] : [];

    // Rows of a loaded document are built here with the page's manage-gates; rows the user adds by
    // hand are built by the table components themselves.
    const positionsDisabled = !this.canManagePositions();
    this.positions.clear();
    if (doc.positions && doc.positions.length > 0) {
      doc.positions.forEach(position => this.positions.push(createPositionGroup(this.fb, position, positionsDisabled)));
    } else {
      this.positions.push(createPositionGroup(this.fb, undefined, positionsDisabled));
    }

    // Unlike Positionen, no empty row is auto-inserted: an empty Fehlererfassung is the normal case.
    this.originalFalsePositions = doc.falsePositions ? [...doc.falsePositions] : [];
    this.falsePositions.clear();
    const falsePositionsDisabled = !this.canManageFalsePositions();
    (doc.falsePositions ?? []).forEach(falsePosition =>
      this.falsePositions.push(createFalsePositionGroup(this.fb, falsePosition, falsePositionsDisabled))
    );

    // Data just came from the server — reset the dirty baseline so unsavedChangesGuard doesn't
    // treat a load/reload as an unsaved change. Also reset the touched baseline: a failed create
    // attempt (missing e.g. Bauteilart) calls markAllAsTouched() on the whole form, and that stale
    // touched flag would otherwise survive the successful create into edit mode.
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private initForm(): void {
    this.form = this.fb.group({
      // Shown read-only — set from the Mitarbeiternummer modal (create) or the loaded document (edit).
      // Deliberately without Validators.required: a read-only control with no error UI would block
      // Speichern without telling anyone why. submit() checks it explicitly instead.
      employeeId: [''],
      tempCustomer: [''],
      palletNumber: [''],
      palletBoxType: [''],
      weDate: [new Date(), Validators.required],
      // Optional — a document may be saved without any Liefertermin.
      deliveryDates: this.fb.array([]),
      order: [null],
      orderQuantity: [null],
      partialQuantity: [null],
      componentType: [''],
      gurtbreite: [null],
      factory: [WerkType.WERK_1, Validators.required],
      processing: [[]],
      activities: [[]],
      remark: [''],
      observeSpecification: [false],
      status: [OrderDocumentStatusEnum.WARENEINGANG, Validators.required],
      positions: this.fb.array([]),
      falsePositions: this.fb.array([]),
    });

    this.newLieferterminForm = this.fb.group({
      date: this.fb.control<Date | null>(null),
    });



    // Tracks whether the Kunde field carries a real customer, so BÜRO-only/Wareneingang-only
    this.form
      .get('order')!
      .valueChanges.pipe(startWith(this.form.get('order')!.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.hasCustomer.set(!!value));

    // BÜRO can't see the tempCustomer field (see showTempCustomerField), so surface it in the page
    // header instead — but only once a document actually exists, never while creating. Once a real
    // Kunde (order) has been assigned, tempCustomer is no longer the relevant identifier — show the
    // Kunde's order id instead, even if tempCustomer still carries a (now stale) value.
    combineLatest([
      this.form.get('order')!.valueChanges.pipe(startWith(this.form.get('order')!.value)),
      this.form.get('tempCustomer')!.valueChanges.pipe(startWith(this.form.get('tempCustomer')!.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([order, tempCustomer]) => {
        if (!this.isEditMode()) {
          this.pageTitleService.setSuffix(null);
          return;
        }
        const suffix = order ? `- ${(order as IOrder).internalOrderId}` : tempCustomer ? `von ${tempCustomer}` : null;
        this.pageTitleService.setSuffix(suffix);
      });
  }

  ngOnDestroy(): void {
    // The title suffix lives in an app-wide service — clear it so it doesn't leak into other pages.
    this.pageTitleService.clear();

    if (this.isEmployeeIdModalOpen) this.modalService.close();

    this.releaseLock();
  }

  public get positions(): FormArray {
    return this.form.get('positions') as FormArray;
  }

  public get deliveryDates(): FormArray {
    return this.form.get('deliveryDates') as FormArray;
  }

  public get falsePositions(): FormArray {
    return this.form.get('falsePositions') as FormArray;
  }

  // Built pre-disabled for Fertigungsmitarbeiter (mirrors createPositionGroup's `disabled` pattern) —
  // the array itself is cleared and every row recreated on each load (see patchFormValues), so an
  // effect() reacting to isFertigungsmitarbeiter() only would miss rows created afterwards.
  public createLieferterminGroup(date: Date, isBinding: boolean, label = ''): FormGroup {
    const disabled = this.isFertigungsmitarbeiter();
    return this.fb.group({
      date: [{ value: date, disabled }, Validators.required],
      isBinding: [{ value: isBinding, disabled }],
      label: [{ value: label, disabled }],
    });
  }

  // Adds the picked date to the deliveryDates FormArray immediately (fired by the datepicker's
  // onSelect). New entries start "nicht sicher"; the user ticks the row's checkbox afterwards.
  // The datepicker control is reset so the popover stays open for adding more dates in a row.
  public addLiefertermin(date?: Date): void {
    // Liefertermine is part of the "upper part" locked for Fertigungsmitarbeiter — the add
    // datepicker's own control is disabled too (see the locked-fields effect), but this guards
    // the action itself in case it's ever reachable another way.
    if (this.isFertigungsmitarbeiter()) return;
    const picked = date ?? this.newLieferterminForm.getRawValue().date;
    if (!picked) return;
    this.deliveryDates.push(this.createLieferterminGroup(picked, false));
    this.deliveryDates.markAsDirty();
    this.deliveryDates.markAsTouched();
    this.newLieferterminForm.reset({ date: null });
  }

  // Typed cast for a row's FormGroup, so the template can bind date/label/isBinding via
  // formControlName under strictTemplates (deliveryDates.controls is AbstractControl[]).
  public asFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  // Removes by the group's actual FormArray index (looked up fresh), not a loop index — the
  // template iterates sortedLiefertermine(), whose order can differ from the FormArray's.
  public removeLieferterminGroup(group: AbstractControl): void {
    // Guards the action itself — the ✕ button isn't a formControlName element, so it's also
    // disabled directly in the template via [disabled]="isFertigungsmitarbeiter()".
    if (this.isFertigungsmitarbeiter()) return;
    const index = this.deliveryDates.controls.indexOf(group as FormGroup);
    if (index === -1) return;
    this.deliveryDates.removeAt(index);
    this.deliveryDates.markAsDirty();
    this.deliveryDates.markAsTouched();
  }

  // Always sorted by date, soonest first. A sorted copy — never mutates the FormArray
  // itself, so form index / persistence order stays untouched.
  public sortedLiefertermine(): FormGroup[] {
    return [...this.deliveryDates.controls].sort(
      (a, b) => (a.get('date')!.value as Date).getTime() - (b.get('date')!.value as Date).getTime()
    ) as FormGroup[];
  }

  // ----- Fehlererfassung (false positions) -----

  // "Fehler erfassen" on a Zeiterfassung row: switches to the Fehlererfassung tab, where the table
  // adds a new row prefilled from the position (see FalsePositionsTableComponent.addRowFromPosition).
  public recordFalsePosition(position: IOrderDocumentPosition): void {
    if (!this.canManageFalsePositions()) return;
    this.selectTab('falsePositions');
    this.falsePositionsTable()?.addRowFromPosition(position);
  }

  private buildFalsePositionPayload(row: IOrderDocumentFalsePosition): IOrderDocumentFalsePosition {
    return {
      posNr: String(row.posNr).trim(),
      date: row.date,
      employeeNumber: String(row.employeeNumber).trim(),
      inspectorNumber: String(row.inspectorNumber ?? '').trim(),
      code: row.code,
      quantity: Number(row.quantity),
      remark: row.remark ?? '',
    };
  }

  // ----- Tabs -----

  // Bound to p-tabs' valueChange, which emits PrimeNG's generic tab value type.
  public onTabChange(value: string | number | undefined): void {
    if (value === 'positions' || value === 'falsePositions' || value === 'documents') {
      this.selectTab(value);
    }
  }

  private selectTab(tab: OrderDocumentTab): void {
    // The pinned Kopfzeile locks <main>'s scrolling for the Zeiterfassung table — release it when
    // leaving that tab, or the other tabs couldn't be scrolled.
    if (tab !== 'positions') {
      this.isHeaderPinned.set(false);
    }
    this.activeTab.set(tab);
  }

  // On a failed save, make sure the user can see the red fields: if the active tab is fine but the
  // other one has errors, switch to it.
  private revealInvalidTab(): void {
    const invalid: Record<OrderDocumentTab, boolean> = {
      positions: this.positions.invalid,
      falsePositions: this.falsePositions.invalid,
      documents: false,
    };
    if (invalid[this.activeTab()]) return;
    if (invalid.positions) this.selectTab('positions');
    else if (invalid.falsePositions) this.selectTab('falsePositions');
  }

  public openTextBlockModal(): void {
    this.modalService
      .open(PasteTextBlockModalComponent, { type: TextBlockType.ABS })
      .then((text: string | null) => {
        if (text) {
          const control = this.form.get('remark');
          const current = (control?.value as string) ?? '';
          control?.setValue(current ? `${current}, ${text}` : text);
          control?.markAsDirty();
        }
        this.modalService.close();
      })
      .catch(() => { });
  }

  private buildPositionPayload(position: IOrderDocumentPosition): Partial<IOrderDocumentPosition> {
    return {
      posNr: position.posNr,
      date: position.date,
      employeeNumber: position.employeeNumber,
      activity: position.activity,
      stueck: position.stueck,
      time: position.time,
      bemerkung: position.bemerkung,
    };
  }

  public isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  // A new row (no _id) that the user never edited — the Positionen row auto-inserted by
  // patchFormValues, or a Fehlererfassung row from "Fehler hinzufügen"/"Fehler erfassen" that was
  // left alone — must not make the whole form invalid, so it's dropped on save. Based on `pristine`
  // rather than on empty values: values prefilled when the row is created (Datum, or Pos./Verursacher
  // copied by "Fehler erfassen") don't mark it dirty, so they don't count as user input. A
  // duplicated Fehlererfassung row is the exception: it's created dirty, so it's always saved.
  // Rows that already exist on the server (they carry an _id) are never pruned.
  private pruneUntouchedNewRows(array: FormArray): void {
    for (let i = array.length - 1; i >= 0; i--) {
      const row = array.at(i);
      if (!row.get('_id')?.value && row.pristine) array.removeAt(i);
    }
  }

  // Used by unsavedChangesGuard (canDeactivate) to decide whether to prompt before navigating away.
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  // Lets unsavedChangesGuard trigger a save on Speichern and learn whether it actually succeeded,
  // without submit() itself needing to change its signature for the normal button click.
  private readonly saveOutcome$ = new Subject<boolean>();

  public saveForNavigation(): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      // Subscribe before calling submit() — submit() can emit synchronously (e.g. on a
      // validation failure), so the subscriber must already be listening.
      this.saveOutcome$.pipe(take(1)).subscribe(resolve);
      this.submit();
    });
  }

  public submit(): void {
    this.pruneUntouchedNewRows(this.positions);
    this.pruneUntouchedNewRows(this.falsePositions);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.revealInvalidTab();
      this.toastService.showError('Bitte füllen Sie alle Pflichtfelder aus');
      this.saveOutcome$.next(false);
      return;
    }

    const { positions: positionRows, falsePositions: falsePositionRows, ...formValue } = this.form.getRawValue();
    const headerPayload = formValue as Partial<IOrderDocument>;
    const docId = this.orderDocumentId();

    if (this.isEditMode() && docId) {
      this.isSubmitting.set(true);

      // Re-validate the lock right before saving — mirrors create-delivery-note.component.ts's
      // submit(), which re-acquires the lock at save time rather than trusting the one taken
      // when the page was opened.
      const user = this.currentUser();
      if (user?.employeeId) {
        this.orderDocumentLockService
          .createLock(docId, String(user.employeeId))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.isLocked.set(false);
              this.lockInfo.set(null);
              this.saveEditedDocument(docId, headerPayload, positionRows, falsePositionRows);
            },
            error: error => {
              this.isLocked.set(true);
              this.lockInfo.set(error.error?.DATA || null);
              this.isSubmitting.set(false);
              this.toastService.showError('Dieser Auftragsbegleitschein wird von einem anderen Benutzer bearbeitet');
              this.saveOutcome$.next(false);
            },
          });
      } else {
        this.saveEditedDocument(docId, headerPayload, positionRows, falsePositionRows);
      }
      return;
    }

    // employeeId is read-only, it comes from the Mitarbeiternummer modal. Without it the backend
    // rejects the create, so fail with a message the user can act on.
    if (!headerPayload.employeeId) {
      this.toastService.showError('Es wurde keine Mitarbeiternummer bestätigt');
      this.saveOutcome$.next(false);
      return;
    }

    this.isSubmitting.set(true);
    // Positionen can't be entered until the document exists (gated by isEditMode() in the template), so there's
    // nothing to persist for them here.
    this.orderDocumentService
      .create(headerPayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.isSubmitting.set(false);
          this.toastService.showSuccess('Auftragsbegleitschein erfolgreich erstellt');
          // Stay on the page and flip into edit mode in place (mirrors create-delivery-note)
          // so a second Speichern click updates the document instead of creating another one.
          // isEditMode flips before patchFormValues so the tempCustomer patch below is picked up
          // as an edit-mode change (see the tempCustomer valueChanges subscription in initForm()).
          this.orderDocumentId.set(response.DATA._id ?? null);
          this.isEditMode.set(true);
          this.patchFormValues(response.DATA);
          this.saveOutcome$.next(true);
        },
        error: () => {
          this.isSubmitting.set(false);
          this.toastService.showError('Fehler beim Erstellen des Auftragsbegleitscheins');
          this.saveOutcome$.next(false);
        },
      });
  }

  // Header, Positionen and Fehlererfassung are persisted through separate backend endpoints (see
  // backend/src/routes/orderDocumentRoutes.ts), but Speichern triggers all of them from one click —
  // row requests are only issued once the document is in edit mode (canManagePositions / canManageFalsePositions).
  private saveEditedDocument(
    docId: string,
    formValue: Partial<IOrderDocument>,
    positionRows: IOrderDocumentPosition[],
    falsePositionRows: IOrderDocumentFalsePosition[]
  ): void {
    const requests: Observable<unknown>[] = [this.orderDocumentService.update(docId, formValue)];

    if (this.canManagePositions()) {
      const currentIds = new Set(positionRows.filter(position => position._id).map(position => position._id));
      const removedIds = this.originalPositions
        .filter(position => position._id && !currentIds.has(position._id))
        .map(position => position._id as string);

      positionRows.forEach(position => {
        const payload = this.buildPositionPayload(position);
        requests.push(
          position._id
            ? this.orderDocumentService.updatePosition(docId, position._id, payload)
            : this.orderDocumentService.addPosition(docId, payload)
        );
      });

      removedIds.forEach(id => requests.push(this.orderDocumentService.deletePosition(docId, id)));
    }

    if (this.canManageFalsePositions()) {
      // Fehlererfassung: same add/update/delete diff, except that stored rows are only written when
      // the user actually changed them. falsePositionRows comes from getRawValue(), so its indices
      // line up with the FormArray's controls.
      const currentFalsePositionIds = new Set(falsePositionRows.filter(row => row._id).map(row => row._id));
      const removedFalsePositionIds = this.originalFalsePositions
        .filter(falsePosition => falsePosition._id && !currentFalsePositionIds.has(falsePosition._id))
        .map(falsePosition => falsePosition._id as string);

      falsePositionRows.forEach((row, index) => {
        const payload = this.buildFalsePositionPayload(row);
        if (!row._id) {
          requests.push(this.orderDocumentService.addFalsePosition(docId, payload));
        } else if (this.falsePositions.at(index).dirty) {
          requests.push(this.orderDocumentService.updateFalsePosition(docId, row._id, payload));
        }
      });

      removedFalsePositionIds.forEach(id => requests.push(this.orderDocumentService.deleteFalsePosition(docId, id)));
    }

    forkJoin(requests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.toastService.showSuccess('Auftragsbegleitschein erfolgreich aktualisiert');
          // Stay on the page. Re-fetch rather than trust positionRows as the new baseline: newly
          // added rows only get a real _id from the server, and forkJoin doesn't surface which
          // response belongs to which row — reloading keeps originalPositions (and the Positionen
          // FormArray's _id controls) correct for the next Speichern click.
          this.loadOrderDocument(docId);
          this.saveOutcome$.next(true);
        },
        error: () => {
          this.isSubmitting.set(false);
          this.toastService.showError('Fehler beim Aktualisieren des Auftragsbegleitscheins');
          this.saveOutcome$.next(false);
        },
      });
  }

  public cancel(): void {
    this.router.navigate(['/order-documents']);
  }

  // ----- Kunde (order) autocomplete -----

  public searchOrders(query: string): void {
    this.isOrderSearchLoading.set(true);
    this.orderSearchTerms.next(query);
  }
}
